import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { FormattedContent } from '../utils/textUtils';
import { enhanceImageUrl } from '../utils/imageEnhancer';
import YouTubeEmbed from '../components/YouTubeEmbed';

const API = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const DynamicDashboard = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  
  const [patternRows, setPatternRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);
  const [activeCategory, setActiveCategory] = useState('all');

  // Categories (same as Dashboard.js)
  const categories = [
    { id: 'all', label: 'All Posts', icon: '🌟' },
    { id: 'personal_observation', label: 'Personal Observation', icon: '👁️' },
    { id: 'consciousness', label: 'Consciousness', icon: '🧠' },
    { id: 'meditation', label: 'Meditation', icon: '🧘' },
    { id: 'health_activities', label: 'Health Activities', icon: '🏃' },
    { id: 'psychedelic_therapy', label: 'Psychedelic Therapy', icon: '🍄' }
  ];

  useEffect(() => {
    // Reset when category changes
    setPatternRows([]);
    setPage(1);
    setHasMore(true);
    fetchDynamicPattern(1, true);
  }, [activeCategory, token]);

  const fetchDynamicPattern = async (pageNum = 1, resetPattern = false) => {
    try {
      if (resetPattern) {
        setLoading(true);
      } else {
        setLoadingMore(true);
      }
      
      const headers = {};
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }

      const params = {
        page: pageNum,
        limit: 6, // 3 standard posts + 3 mixed rows
        ...(activeCategory !== 'all' ? { category: activeCategory } : {})
      };
      
      const response = await axios.get(`${API}/api/feed/dynamic-pattern`, { headers, params });
      const data = response.data;
      
      if (resetPattern) {
        setPatternRows(data.pattern_rows || []);
      } else {
        setPatternRows(prev => [...prev, ...(data.pattern_rows || [])]);
      }

      setHasMore(data.has_more || false);

    } catch (error) {
      console.error('Error fetching dynamic pattern:', error);
      if (resetPattern) {
        setPatternRows([]);
      }
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const loadMore = () => {
    if (loadingMore || !hasMore) return;
    const nextPage = page + 1;
    setPage(nextPage);
    fetchDynamicPattern(nextPage, false);
  };

  // Render standard post (2x2)
  const renderStandardPost = (post) => {
    const hasImage = post.image || (post.content_type === 'video' && post.youtube_thumbnail);
    
    return (
      <div className="bg-white rounded-xl shadow-md overflow-hidden border p-6">
        {/* Post Header */}
        <div className="flex items-center space-x-3 mb-4">
          <div className="w-10 h-10 bg-gradient-to-br from-emerald-400 to-teal-500 rounded-full flex items-center justify-center text-white font-bold">
            {post.username ? post.username[0].toUpperCase() : 'M'}
          </div>
          <div className="flex-1">
            <div className="font-semibold text-gray-900">
              {post.username || 'Modern Human Being'}
            </div>
            <div className="text-sm text-gray-500">
              {new Date(post.created_at).toLocaleDateString()}
            </div>
          </div>
          {post.is_official && (
            <span className="px-3 py-1 bg-emerald-100 text-emerald-700 rounded-full text-sm font-medium">
              ✨ Official
            </span>
          )}
        </div>

        {/* Post Title */}
        {post.title && (
          <h3 className="font-bold text-gray-900 text-xl leading-tight mb-4">
            {post.title}
          </h3>
        )}

        {/* YouTube Video */}
        {post.content_type === 'video' && post.video_url && (
          <div className="mb-4">
            <YouTubeEmbed 
              videoUrl={post.video_url}
              title={post.youtube_title || post.title}
            />
          </div>
        )}

        {/* Post Image */}
        {hasImage && post.content_type !== 'video' && (
          <div className="mb-4">
            <img
              src={enhanceImageUrl(post.image)}
              alt={post.title || 'Post image'}
              className="w-full h-auto object-cover rounded-lg"
              style={{ maxHeight: '400px' }}
            />
          </div>
        )}

        {/* Post Content */}
        {post.content && (
          <div className="text-gray-700 mb-4 leading-relaxed">
            <FormattedContent content={post.content} />
          </div>
        )}

        {/* Engagement Stats */}
        <div className="flex items-center justify-between pt-4 border-t border-gray-100">
          <div className="flex items-center space-x-4 text-gray-500">
            <span className="flex items-center space-x-1">
              <span>❤️</span>
              <span>{post.likes_count || 0}</span>
            </span>
            <span className="flex items-center space-x-1">
              <span>💬</span>
              <span>{post.comments_count || 0}</span>
            </span>
          </div>
          <div className="text-sm text-gray-500">
            {post.categories?.[0] && `#${post.categories[0]}`}
          </div>
        </div>
      </div>
    );
  };

  // Render mixed row item
  const renderMixedRowItem = (item) => {
    const { type, content, width } = item;

    switch (type) {
      case 'text_only_post':
        return (
          <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 h-32 flex flex-col justify-center">
            <div className="font-medium text-purple-800 text-sm mb-2">
              @{content.username || 'Anonymous'}
            </div>
            <div className="text-gray-700 text-sm line-clamp-3">
              <FormattedContent content={content.content} />
            </div>
          </div>
        );

      case 'teaser_post':
        return (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 h-32 flex flex-col justify-center cursor-pointer hover:bg-green-100 transition-colors">
            <div className="font-medium text-green-800 text-sm mb-1">
              {content.title}
            </div>
            <div className="text-gray-600 text-xs line-clamp-2">
              {content.content}
            </div>
            <div className="text-green-600 text-xs mt-2">
              Click to view original →
            </div>
          </div>
        );

      case 'house_ad':
        return (
          <div className="bg-gradient-to-br from-emerald-500 to-teal-600 text-white rounded-lg p-4 h-32 flex flex-col justify-center text-center">
            <div className="font-bold text-sm mb-1">🌿 HOUSE AD</div>
            <div className="text-xs opacity-90 mb-1">{content.title || content.name}</div>
            {content.price && (
              <div className="font-bold text-lg">${content.price}</div>
            )}
          </div>
        );

      case 'affiliate_banner':
        return (
          <div className="bg-gradient-to-br from-purple-600 to-indigo-700 text-white rounded-lg p-4 h-32 flex flex-col justify-center text-center cursor-pointer hover:scale-105 transition-transform">
            <div className="font-bold mb-1">🔗 {content.title}</div>
            {content.description && (
              <div className="text-xs opacity-90 mb-2">{content.description}</div>
            )}
            <div className="text-xs bg-white bg-opacity-20 rounded px-2 py-1 inline-block">
              Learn More
            </div>
          </div>
        );

      case 'filler':
        return (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 h-32 flex flex-col justify-center">
            <div className="font-medium text-blue-800 text-sm mb-1">
              💡 {content.title || 'Did You Know?'}
            </div>
            <div className="text-gray-700 text-xs line-clamp-3">
              {content.content}
            </div>
          </div>
        );

      case 'mini_aff':
        const heightClass = width === 1 ? 'h-32' : width === 2 ? 'h-40' : 'h-48';
        return (
          <div className={`bg-gradient-to-br from-yellow-200 to-orange-300 rounded-lg p-4 ${heightClass} flex items-center justify-center`}>
            <div className="text-gray-800 font-medium text-center italic">
              {content.content}
            </div>
          </div>
        );

      default:
        return (
          <div className="bg-gray-100 border border-gray-300 rounded-lg p-4 h-32 flex items-center justify-center text-gray-500 text-sm">
            Unknown: {type}
          </div>
        );
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-teal-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-emerald-500 mx-auto mb-4"></div>
          <p className="text-emerald-700 font-medium">Loading dynamic feed...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-teal-50">
      <div className="container mx-auto px-4 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          
          {/* Left Sidebar - Categories */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-xl shadow-lg p-6 sticky top-6">
              <h3 className="font-bold text-gray-900 mb-4">🌟 Areas of Study</h3>
              <div className="space-y-2">
                {categories.map(category => (
                  <button
                    key={category.id}
                    onClick={() => setActiveCategory(category.id)}
                    className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                      activeCategory === category.id
                        ? 'bg-emerald-500 text-white'
                        : 'text-gray-700 hover:bg-emerald-50'
                    }`}
                  >
                    <span className="mr-2">{category.icon}</span>
                    {category.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Main Feed - Dynamic Pattern */}
          <div className="lg:col-span-8">
            <div className="space-y-6">
              
              {/* Header */}
              <div className="bg-white rounded-xl shadow-lg p-6">
                <h1 className="text-2xl font-bold text-gray-900 mb-2">
                  🧱 Dynamic Pattern Feed
                </h1>
                <p className="text-gray-600">
                  Brick wall layout with varied content blocks and consistent spacing
                </p>
                <div className="mt-4 flex items-center gap-4 text-sm text-gray-500">
                  <span>📊 Pattern: Standard Post → Mixed Row → Repeat</span>
                  <span>🎯 Banner Alignment: 2-col right, 3-col center</span>
                </div>
              </div>

              {/* Dynamic Pattern Rows */}
              {patternRows.map((row, rowIndex) => (
                <div key={rowIndex} className="pattern-row">
                  {row.type === 'standard_post' ? (
                    // Standard Post (2 rows worth of height)
                    <div className="standard-post mb-6">
                      {renderStandardPost(row.content)}
                    </div>
                  ) : (
                    // Mixed Row (1 row height)
                    <div className="mixed-row mb-6">
                      <div className="grid grid-cols-4 gap-4">
                        {row.content.map((item, itemIndex) => (
                          <div 
                            key={itemIndex}
                            className={`
                              ${item.width === 1 ? 'col-span-1' : 
                                item.width === 2 ? 'col-span-2' : 
                                item.width === 3 ? 'col-span-3' : 'col-span-4'}
                              ${item.alignment === 'right' ? 'col-start-3' : ''}
                            `}
                          >
                            {renderMixedRowItem(item)}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}

              {/* Load More */}
              {hasMore && (
                <div className="text-center py-8">
                  <button
                    onClick={loadMore}
                    disabled={loadingMore}
                    className="bg-emerald-500 hover:bg-emerald-600 text-white px-8 py-3 rounded-lg font-medium disabled:opacity-50 transition-colors"
                  >
                    {loadingMore ? (
                      <>
                        <span className="animate-spin mr-2">⏳</span>
                        Loading more...
                      </>
                    ) : (
                      'Load More Content'
                    )}
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Right Sidebar */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-xl shadow-lg p-6 sticky top-6">
              <h3 className="font-bold text-gray-900 mb-4">🌿 Conscious Commerce</h3>
              <div className="space-y-4">
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 text-center">
                  <div className="text-emerald-800 font-medium">Eco-Friendly Products</div>
                  <div className="text-sm text-emerald-600 mt-1">Coming Soon</div>
                </div>
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 text-center">
                  <div className="text-purple-800 font-medium">Sacred Geometry</div>
                  <div className="text-sm text-purple-600 mt-1">Art & Jewelry</div>
                </div>
              </div>
              
              <div className="mt-6 pt-6 border-t border-gray-200">
                <button
                  onClick={() => navigate('/dashboard')}
                  className="w-full bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-lg text-sm transition-colors"
                >
                  ← Back to Classic View
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DynamicDashboard;